import {useCallback} from 'react';

import {marketT} from '@/lib/types/market.types';

import {SupabaseInstance} from '@/lib/utils';

import {useAuth} from '@/providers/components/authProvider';
import {
  BaseContextT,
  createAppContext,
  createAppHook,
  createAppProvider,
  createProviderConfig,
  useBaseContext,
} from '@/providers/components/baseProvider';

// Config
const {table, columns, statsView} = createProviderConfig({
  table: 'Market',
  statsView: 'market_stats',
});

type TableT = typeof table;
type ColumnsT = typeof columns;
type StatsViewT = typeof statsView;
type CustomContextT = {
  getMarket: (id: number) => Promise<marketT>;
  getDistributorIdsForMarket: (marketId: number) => Promise<number[] | undefined>;
};

// Context
type MarketsContextT = BaseContextT<TableT, StatsViewT, ColumnsT, CustomContextT>;
const MarketsContext = createAppContext<MarketsContextT>(table);

// Provider
const ContextProvider = ({children}: {children: React.ReactNode}) => {
  const {isAdministrator} = useAuth();
  const baseContext = useBaseContext<TableT, ColumnsT, StatsViewT>();

  /*******************************************************************
   * Gets a market record from the database.
   * @param {number} id - The ID of the market to get.
   * @returns {Promise<marketT>} - The market object.
   * @throws {Error} - If there's an error in getting the market.
   ********************************************************************/
  const getMarket = useCallback(
    async (id: number) => {
      if (!isAdministrator) throw new Error('Error getting retailer');
      try {
        const {data, error} = await SupabaseInstance.supabase.from('Market').select().eq('market_id', id).single();
        if (!data || error) throw new Error('Market not found');
        return data;
      } catch (error) {
        throw new Error((error as Error).message);
      }
    },
    [isAdministrator]
  );

  const getDistributorIdsForMarket = async (marketId: number) => {
    if (!marketId) {
      return;
    }
    // Get all of the distributors for the market
    const {data, error} = await SupabaseInstance.supabase
      .from('Market_Distributor')
      .select('*')
      .eq('fk_market_id', marketId)
      .is('date_removed', null);

    if (error || !data) {
      console.error('Error fetching market distributors:', error);
      return;
    }

    // get all distributor ids from the market distributors
    const distributorIds = data.map(md => md.fk_distributor_id);

    return distributorIds;
  };

  return (
    <MarketsContext.Provider
      value={{
        ...baseContext,
        getMarket,
        getDistributorIdsForMarket,
      }}
    >
      {children}
    </MarketsContext.Provider>
  );
};

// Add this to the AppProviders.tsx file
export const MarketsProvider = createAppProvider({
  table,
  columns,
  statsView,
  ContextProvider,
});

// Hook
export const useMarkets = createAppHook<TableT, StatsViewT, ColumnsT, CustomContextT>(MarketsContext);
